if (!customElements.get("quick-add-modal")) {
  customElements.define(
    "quick-add-modal",
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();

        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        // Close when user clicks on the overlay (outside the panel)
        this.addEventListener("click", (event) => {
          const panel = this.querySelector(".quick-add-modal__content");
          if (!panel) return;

          if (this.hasAttribute("open") && !panel.contains(event.target)) {
            this.hide(true);
          }
        });

        window.addEventListener("keyup", (event) => {
          if (event.code && event.code.toUpperCase() === "ESCAPE") this.hide(true);
        });
      }

      hide(preventFocus = false) {
        document.body.classList.remove("quick-add-open");

        // Destroy Swiper instances created inside the modal only
        this.modalContent?.querySelectorAll(".qa-slider.swiper").forEach((el) => {
          try {
            el.swiper?.destroy(true, true);
          } catch (e) {}
        });

        // Clear injected HTML
        if (this.modalContent) this.modalContent.innerHTML = "";

        if (preventFocus) this.openedBy = null;
        super.hide(preventFocus);
      }

      show(opener) {
        opener?.setAttribute("aria-disabled", true);
        opener?.classList.add("loading");

        const spinner = opener?.querySelector?.(".loading-overlay__spinner");
        if (spinner) spinner.classList.remove("hidden");

        const rawUrl = opener?.getAttribute?.("data-product-url") || "";

        this.loadQuickAddDrawer(rawUrl)
          .then(() => {
            // Match drawer width to a single product card on the collection grid
            const card = document.querySelector(".collection-product-card");
            const panel = this.querySelector(".quick-add-modal__content");
            if (card && panel) {
              panel.style.maxWidth = (card.offsetWidth + 4) + "px";
            }

            document.body.classList.add("quick-add-open");
            super.show(opener);
          })
          .catch(() => {
            // Fail silently to keep theme stable
          })
          .finally(() => {
            opener?.removeAttribute?.("aria-disabled");
            opener?.classList.remove("loading");
            if (spinner) spinner.classList.add("hidden");
          });
      }

      // Build /products/... URL that returns ONLY the quick-add-drawer section HTML.
      buildSectionUrl(rawUrl) {
        if (!rawUrl) return "";

        let url;
        try {
          url = new URL(rawUrl, window.location.origin);
        } catch (e) {
          return "";
        }

        if (!url.searchParams.has("section_id")) {
          url.searchParams.set("section_id", "quick-add-drawer");
        }

        return url.toString();
      }

      // Fetch and mount the quick-add-drawer section HTML into the modal.
      async loadQuickAddDrawer(rawUrl) {
        const sectionUrl = this.buildSectionUrl(rawUrl);
        if (!sectionUrl || !this.modalContent) {
          console.error('[QuickAdd] Invalid URL or modal content missing', { sectionUrl, modalContent: this.modalContent });
          return;
        }

        // Clean up previous instances before re-mount
        this.modalContent.querySelectorAll(".qa-slider.swiper").forEach((el) => {
          try {
            el.swiper?.destroy(true, true);
          } catch (e) {}
        });

        try {
          const response = await fetch(sectionUrl, { credentials: "same-origin" });

          if (!response.ok) {
            console.error('[QuickAdd] Fetch failed:', response.status, response.statusText);
            throw new Error(`HTTP ${response.status}`);
          }

          const htmlText = await response.text();

          const doc = new DOMParser().parseFromString(htmlText, "text/html");

          // Shopify section response wrapper
          const sectionRoot =
            doc.querySelector("#shopify-section-quick-add-drawer") ||
            doc.querySelector(".shopify-section") ||
            doc.body;

          this.setInnerHTML(this.modalContent, sectionRoot.innerHTML);

          // Init interactive parts
          this.initDrawer();
        } catch (error) {
          console.error('[QuickAdd] Error loading drawer:', error);
          this.modalContent.innerHTML = '<div style="padding: 2rem; text-align: center;">Failed to load product details. Please try again.</div>';
        }
      }

      // Replace innerHTML while re-injecting scripts (Shopify convention).
      setInnerHTML(element, html) {
        element.innerHTML = html;

        element.querySelectorAll("script").forEach((oldScriptTag) => {
          const newScriptTag = document.createElement("script");
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });
      }

      initDrawer() {
        const root = this.modalContent?.querySelector(".qa-drawer");
        if (!root) return;

        this.initSwiper(root);
        this.initQuantity(root);
        this.initVariants(root);
        this.initCustomSelect(root);
        this.initSwatchSwitching(root);
        this.initFormSubmit(root);
      }

      initSwiper(root) {
        const sliderEl = root.querySelector(".qa-slider.swiper");
        if (!sliderEl) return;

        if (typeof window.Swiper !== "function") return;

        try {
          sliderEl.swiper?.destroy(true, true);
        } catch (e) {}

        const prevBtn = root.querySelector(".qa-slider__nav--prev .qa-slider__arrow");
        const nextBtn = root.querySelector(".qa-slider__nav--next .qa-slider__arrow");
        const paginationEl = root.querySelector(".qa-slider__pagination");

        // eslint-disable-next-line no-new
        new window.Swiper(sliderEl, {
          slidesPerView: "auto",
          spaceBetween: 4,
          watchOverflow: true,
          loop: false,
          navigation: {
            prevEl: prevBtn,
            nextEl: nextBtn
          },
          pagination: paginationEl
            ? {
                el: paginationEl,
                clickable: true
              }
            : undefined
        });
      }

      initQuantity(root) {
        const input = root.querySelector(".qa-quantity__input");
        const minus = root.querySelector(".qa-quantity__btn--minus");
        const plus = root.querySelector(".qa-quantity__btn--plus");
        if (!input || !minus || !plus) return;

        const clamp = (n) => {
          const v = parseInt(n, 10);
          if (Number.isNaN(v) || v < 1) return 1;
          return v;
        };

        minus.addEventListener("click", () => {
          const next = clamp(input.value) - 1;
          input.value = String(next < 1 ? 1 : next);
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });

        plus.addEventListener("click", () => {
          input.value = String(clamp(input.value) + 1);
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });

        input.addEventListener("input", () => {
          input.value = String(clamp(input.value));
        });
      }

      initVariants(root) {
        const jsonEl = root.querySelector('script[id^="qa-product-json-"]');
        if (!jsonEl) return;

        let data;
        try {
          data = JSON.parse(jsonEl.textContent || "{}");
        } catch (e) {
          return;
        }

        const idInput = root.querySelector(".qa-variant-id");
        const selects = Array.from(root.querySelectorAll(".qa-select"));
        const priceWrap = root.querySelector(".qa-title-row__price");
        const addBtn = root.querySelector('button[name="add"], .qa-submit-btn');

        if (!idInput || !priceWrap || !data?.variants?.length) return;

        const formatMoney = (cents) => {
          try {
            if (window.Shopify && typeof Shopify.formatMoney === "function") {
              return Shopify.formatMoney(cents, window.Shopify.money_format);
            }
          } catch (e) {}
          return (cents / 100).toFixed(2);
        };

        const getVariantById = (id) =>
          data.variants.find((v) => String(v.id) === String(id));

        const getSelectedOptions = (currentVariant) => {
          const selected = Array.isArray(currentVariant?.options)
            ? [...currentVariant.options]
            : [];

          selects.forEach((sel) => {
            const idx = parseInt(sel.getAttribute("data-option-index"), 10);
            if (!Number.isNaN(idx)) selected[idx] = sel.value;
          });

          return selected;
        };

        const findMatchingVariant = (selectedOptions) =>
          data.variants.find((v) => {
            if (!Array.isArray(v.options)) return false;
            if (v.options.length !== selectedOptions.length) return false;
            return v.options.every((opt, i) => String(opt) === String(selectedOptions[i]));
          });

        const renderPrice = (variant) => {
          const price = variant?.price ?? 0;
          const compare = variant?.compare_at_price ?? 0;

          if (compare > price) {
            priceWrap.innerHTML = `
              <span class="qa-price qa-price--compare">${formatMoney(compare)}</span>
              <span class="qa-price qa-price--sale">${formatMoney(price)}</span>
            `;
          } else {
            priceWrap.innerHTML = `
              <span class="qa-price">${formatMoney(price)}</span>
            `;
          }
        };

        const applyVariant = (variant) => {
          if (!variant) return;

          idInput.value = variant.id;
          renderPrice(variant);

          if (addBtn) addBtn.disabled = !variant.available;
        };

        // Initial state
        const initialVariant = getVariantById(idInput.value) || data.variants[0];
        applyVariant(initialVariant);

        // React on dropdown changes
        selects.forEach((sel) => {
          sel.addEventListener("change", () => {
            const current = getVariantById(idInput.value) || data.variants[0];
            const selectedOptions = getSelectedOptions(current);
            const nextVariant = findMatchingVariant(selectedOptions);
            applyVariant(nextVariant);
            this.syncCustomSelects(root);
          });
        });
      }

      // Custom select UI (pretty dropdown) - syncs to hidden native <select>
      initCustomSelect(root) {
        const widgets = Array.from(root.querySelectorAll(".qa-custom-select"));
        if (!widgets.length) return;

        const closeAll = () => {
          widgets.forEach((w) => {
            const btn = w.querySelector(".qa-custom-select__button");
            const dd = w.querySelector(".qa-custom-select__dropdown");
            if (!btn || !dd) return;
            dd.setAttribute("hidden", "");
            btn.setAttribute("aria-expanded", "false");
            w.classList.remove("is-open");
          });
        };

        // Close dropdowns when clicking anywhere else inside the drawer body
        root.addEventListener(
          "click",
          (e) => {
            const inside = e.target.closest(".qa-custom-select");
            if (!inside) closeAll();
          },
          true
        );

        widgets.forEach((w) => {
          const btn = w.querySelector(".qa-custom-select__button");
          const textEl = w.querySelector(".qa-custom-select__text");
          const dd = w.querySelector(".qa-custom-select__dropdown");
          const select = w.querySelector("select.qa-select");
          const options = Array.from(w.querySelectorAll(".qa-custom-select__option"));
          if (!btn || !textEl || !dd || !select || !options.length) return;

          const sync = () => {
            const val = select.value;
            textEl.textContent = val || "";
            options.forEach((li) => {
              li.setAttribute(
                "aria-selected",
                li.getAttribute("data-value") === val ? "true" : "false"
              );
            });
          };

          sync();

          btn.addEventListener("click", (e) => {
            e.preventDefault();
            const isOpen = !dd.hasAttribute("hidden");

            closeAll();
            if (!isOpen) {
              dd.removeAttribute("hidden");
              btn.setAttribute("aria-expanded", "true");
              w.classList.add("is-open");
            }
          });

          options.forEach((li) => {
            li.addEventListener("click", () => {
              const val = li.getAttribute("data-value");
              if (!val) return;

              select.value = val;
              select.dispatchEvent(new Event("change", { bubbles: true }));

              sync();

              dd.setAttribute("hidden", "");
              btn.setAttribute("aria-expanded", "false");
              w.classList.remove("is-open");
            });
          });
        });
      }

      // Sync custom select labels from current native select values
      syncCustomSelects(root) {
        const widgets = Array.from(root.querySelectorAll(".qa-custom-select"));
        widgets.forEach((w) => {
          const textEl = w.querySelector(".qa-custom-select__text");
          const select = w.querySelector("select.qa-select");
          const options = Array.from(w.querySelectorAll(".qa-custom-select__option"));
          if (!textEl || !select) return;

          const val = select.value;
          textEl.textContent = val || "";

          options.forEach((li) => {
            li.setAttribute(
              "aria-selected",
              li.getAttribute("data-value") === val ? "true" : "false"
            );
          });
        });
      }

      // Combined listings swatch switching inside the modal
      initSwatchSwitching(root) {
        const swatches = Array.from(root.querySelectorAll(".qa-color-swatches a.qa-color-swatch"));
        if (!swatches.length) return;

        swatches.forEach((a) => {
          a.addEventListener("click", (event) => {
            event.preventDefault();

            const url = a.getAttribute("data-product-url") || a.getAttribute("href");
            if (!url) return;

            if (a.classList.contains("qa-color-swatch--active")) return;

            // Preserve current dropdown selections by option index
            const saved = {};
            root.querySelectorAll(".qa-select").forEach((sel) => {
              const idx = sel.getAttribute("data-option-index");
              if (idx != null) saved[idx] = sel.value;
            });

            this.loadQuickAddDrawer(url).then(() => {
              const nextRoot = this.modalContent?.querySelector(".qa-drawer");
              if (!nextRoot) return;

              // Restore saved dropdown values where possible
              Object.keys(saved).forEach((idx) => {
                const sel = nextRoot.querySelector(`.qa-select[data-option-index="${idx}"]`);
                if (!sel) return;

                const canSet = Array.from(sel.options || []).some((opt) => opt.value === saved[idx]);
                if (canSet) sel.value = saved[idx];
              });

              // Trigger variant recalculation
              const firstSelect = nextRoot.querySelector(".qa-select");
              if (firstSelect) firstSelect.dispatchEvent(new Event("change", { bubbles: true }));

              // Sync pretty dropdown text after restoring values
              this.syncCustomSelects(nextRoot);

              // Reset scroll position
              const body = this.modalContent?.querySelector(".qa-drawer__body");
              if (body) body.scrollTop = 0;
            });
          });
        });
      }

      // Handle form submission via AJAX to prevent page redirect
      initFormSubmit(root) {
        const form = root.querySelector(".qa-form");
        if (!form) return;

        form.addEventListener("submit", async (event) => {
          event.preventDefault();

          const submitBtn = form.querySelector(".qa-submit-btn");
          const btnText = submitBtn?.querySelector(".qa-submit-btn__text");
          const btnSpinner = submitBtn?.querySelector(".qa-submit-btn__spinner");

          if (submitBtn?.disabled) return;

          // Disable button and show spinner
          if (submitBtn) submitBtn.disabled = true;
          if (btnText) btnText.classList.add("hidden");
          if (btnSpinner) btnSpinner.classList.remove("hidden");

          try {
            const formData = new FormData(form);

            // Add sections to render (for cart drawer update)
            const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
            if (cart && typeof cart.getSectionsToRender === 'function') {
              formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
              formData.append('sections_url', window.location.pathname);
            }

            const response = await fetch(window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart/add.js` : '/cart/add.js', {
              method: 'POST',
              body: formData,
            });

            const data = await response.json();

            if (data.status) {
              // Error adding to cart
              console.error('[QuickAdd] Cart add error:', data);
              return;
            }

            // Success - dispatch cart update event
            document.dispatchEvent(
              new CustomEvent("cart:change", {
                detail: data,
                bubbles: true,
              })
            );

            // Update cart drawer if it exists
            if (cart && typeof cart.renderContents === 'function') {
              cart.renderContents(data);
            }

            // Close the modal
            this.hide(true);

          } catch (error) {
            console.error('[QuickAdd] Form submit error:', error);
          } finally {
            // Re-enable button and hide spinner
            if (submitBtn) submitBtn.disabled = false;
            if (btnText) btnText.classList.remove("hidden");
            if (btnSpinner) btnSpinner.classList.add("hidden");
          }
        });
      }
    }
  );
}
